import { LOCALES } from "@/lib/i18n";
// Central date/time helpers — all site-wide date formatting flows through
// this module so a change here propagates everywhere without duplicating
// logic. Timezone is always Asia/Gaza (Palestine).

const TZ = "Asia/Gaza";
const AR_LOCALE = "ar-EG-u-nu-latn"; // Arabic labels, Latin (0-9) digits

const AR_WEEKDAYS = [
    "الأحد", "الاثنين", "الثلاثاء", "الأربعاء",
    "الخميس", "الجمعة", "السبت",
];
const AR_MONTHS = [
    "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
    "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

// Convert an ISO timestamp to the equivalent {y,m,d,weekday} in Gaza tz.
// We use Intl to derive the wall-clock date parts, then also compute the
// weekday from a Date built from those parts (safe against locale quirks).
function gazaParts(iso) {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(d);
    const get = (t) => parts.find((p) => p.type === t)?.value;
    const y = Number(get("year"));
    const m = Number(get("month"));
    const day = Number(get("day"));
    // Build a *UTC* date pinned to Gaza's Y-M-D — the weekday is stable
    // because the Y-M-D triple is the Gaza wall-clock date.
    const wd = new Date(Date.UTC(y, m - 1, day)).getUTCDay();
    return { y, m, d: day, weekday: wd, iso: `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}` };
}

// The Gaza-local Y-M-D "today" and "yesterday" for grouping.
function todayGaza() { return gazaParts(new Date().toISOString()); }
function yesterdayGaza() {
    const t = todayGaza();
    const d = new Date(Date.UTC(t.y, t.m - 1, t.d));
    d.setUTCDate(d.getUTCDate() - 1);
    return {
        y: d.getUTCFullYear(),
        m: d.getUTCMonth() + 1,
        d: d.getUTCDate(),
        weekday: d.getUTCDay(),
        iso: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`,
    };
}

// "الاثنين 31 أغسطس 2026" / "Monday 31 August 2026"
export function gazaDateLabel(iso, lang = "ar") {
    const p = gazaParts(iso);
    if (!p) return "";
    if (lang !== "ar") {
        try { return new Date(iso).toLocaleDateString(LOCALES[lang] || "en-GB", { timeZone: TZ, weekday: "long", day: "numeric", month: "long", year: "numeric" }).replace(",", ""); } catch { return ""; }
    }
    return `${AR_WEEKDAYS[p.weekday]} ${p.d} ${AR_MONTHS[p.m - 1]} ${p.y}`;
}

// The prefix for a day divider in the latest-news column.
// Returns { prefix, label } — e.g. { prefix: "اليوم", label: "الاثنين 31 أغسطس 2026" }.
export function gazaDayDivider(iso, lang = "ar") {
    const p = gazaParts(iso);
    if (!p) return { prefix: "", label: "" };
    const t = todayGaza();
    const y = yesterdayGaza();
    let prefix = "";
    if (p.iso === t.iso) prefix = ({ en: "Today", es: "Hoy", nl: "Vandaag" })[lang] || "اليوم";
    else if (p.iso === y.iso) prefix = ({ en: "Yesterday", es: "Ayer", nl: "Gisteren" })[lang] || "أمس";
    return { prefix, label: gazaDateLabel(iso, lang), dayKey: p.iso };
}

// "01:49 م" — no seconds, Gaza time, Latin digits.
export function gazaTimeShort(iso, lang = "ar") {
    try {
        return new Date(iso).toLocaleTimeString(lang !== "ar" ? (LOCALES[lang] || "en-GB") : AR_LOCALE, {
            timeZone: TZ, hour: "2-digit", minute: "2-digit", hour12: true,
        });
    } catch { return ""; }
}

// "3:25 م" — 12-hour, no leading zero, Gaza timezone.
export function gazaTime12(iso) {
    try {
        return new Date(iso).toLocaleTimeString(AR_LOCALE, {
            timeZone: TZ, hour: "numeric", minute: "2-digit", hour12: true,
        });
    } catch { return ""; }
}

// "3 سبتمبر 2026، 3:25 م" — the ONE publish-time format used by news cards
// (home + sections) and the article page. Real published_at only.
export function gazaDateTime(iso, lang = "ar") {
    const p = gazaParts(iso);
    if (!p) return "";
    if (lang !== "ar") {
        // Same Gaza instant, English display only (values never change).
        try {
            return new Date(iso).toLocaleString(LOCALES[lang] || "en-GB", { timeZone: TZ, day: "numeric", month: "long", year: "numeric", hour: "numeric", minute: "2-digit", hour12: lang === "en" });
        } catch { return ""; }
    }
    const t = gazaTime12(iso);
    return `${p.d} ${AR_MONTHS[p.m - 1]} ${p.y}${t ? `، ${t}` : ""}`;
}

// Article page — same canonical format.
export function gazaDateTimeArticle(iso, lang = "ar") {
    return gazaDateTime(iso, lang);
}

// Group a list of items by their Gaza-local Y-M-D. Order is preserved.
export function groupByGazaDay(items, isoKey = "published_at") {
    const groups = [];
    const byKey = new Map();
    for (const it of items || []) {
        const iso = it?.[isoKey] || it?.created_at;
        if (!iso) continue;
        const p = gazaParts(iso);
        if (!p) continue;
        let g = byKey.get(p.iso);
        if (!g) {
            g = { dayKey: p.iso, sampleIso: iso, items: [] };
            byKey.set(p.iso, g);
            groups.push(g);
        }
        g.items.push(it);
    }
    return groups;
}
